import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import Loader from '../components/Loader';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPaperPlane, FaInbox, FaTrash } from 'react-icons/fa';

const Messages = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newMessage, setNewMessage] = useState({
    to: '',
    subject: '',
    body: ''
  });

  useEffect(() => {
    // Simulate loading messages
    setTimeout(() => {
      setLoading(false);
      // Mock data for now
      setMessages([
        {
          id: 1,
          from: 'Dr. Smith',
          to: user?.name || 'Dentist',
          subject: 'Patient Consultation Request',
          body: 'I would like to discuss a patient case with you.',
          timestamp: new Date().toISOString(),
          read: false,
          type: 'inbox'
        },
        {
          id: 2,
          from: user?.name || 'Dentist',
          to: 'Dr. Johnson',
          subject: 'Treatment Plan Review',
          body: 'Please review the attached treatment plan.',
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          read: true,
          type: 'sent'
        }
      ]);
    }, 1000);
  }, [user]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    // Add message to sent items
    const sentMessage = {
      id: Date.now(),
      from: user?.name || 'Dentist',
      to: newMessage.to,
      subject: newMessage.subject,
      body: newMessage.body,
      timestamp: new Date().toISOString(),
      read: true,
      type: 'sent'
    };
    setMessages(prev => [sentMessage, ...prev]);
    setNewMessage({ to: '', subject: '', body: '' });
    setComposeOpen(false);
  };

  const handleDeleteMessage = (messageId) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
    if (selectedMessage?.id === messageId) {
      setSelectedMessage(null);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[rgba(0,119,182,0.05)] to-white">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6]">
                  Messages
                </h1>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setComposeOpen(true)}
                  className="bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl flex items-center"
                >
                  <FaPaperPlane className="mr-2" />
                  Compose
                </motion.button>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Message List */}
              <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-[rgba(0,119,182,0.1)]">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-[#0077B6] flex items-center">
                    <FaInbox className="mr-2" />
                    Inbox
                  </h2>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {messages.filter(msg => msg.type === 'inbox').map((message) => (
                    <motion.div
                      key={message.id}
                      whileHover={{ scale: 1.02 }}
                      className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                        selectedMessage?.id === message.id ? 'bg-blue-50 border-l-4 border-l-[#0077B6]' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedMessage(message)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-900 truncate">{message.subject}</h3>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMessage(message.id);
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{message.from}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(message.timestamp).toLocaleDateString()}
                      </p>
                      {!message.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Message Content */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-[rgba(0,119,182,0.1)]">
                {selectedMessage ? (
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h2 className="text-xl font-semibold text-[#0077B6] mb-2">
                          {selectedMessage.subject}
                        </h2>
                        <div className="text-sm text-gray-600">
                          <p><strong>From:</strong> {selectedMessage.from}</p>
                          <p><strong>To:</strong> {selectedMessage.to}</p>
                          <p><strong>Date:</strong> {new Date(selectedMessage.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-800 whitespace-pre-wrap">{selectedMessage.body}</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    <FaEnvelope className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Select a message to read</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Compose Message Modal */}
      {composeOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#0077B6]">Compose Message</h2>
                <button
                  onClick={() => setComposeOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSendMessage} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                  <input
                    type="text"
                    value={newMessage.to}
                    onChange={(e) => setNewMessage(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <input
                    type="text"
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    value={newMessage.body}
                    onChange={(e) => setNewMessage(prev => ({ ...prev, body: e.target.value }))}
                    rows="6"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setComposeOpen(false)}
                    className="px-6 py-2 border border-[#20B2AA] text-[#20B2AA] rounded-full hover:bg-[rgba(32,178,170,0.1)] font-medium transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Messages;
